/* eslint-disable warp-drive/no-legacy-request-patterns */
import Service, { service } from '@ember/service';
import { get } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type Value from '@linc-technologies/linced-common/models/dynamic-field/value';
import type Field from '@linc-technologies/linced-common/models/school-options/field';
import type Group from '@linc-technologies/linced-common/models/school-options/group';
import type Heading from '@linc-technologies/linced-common/models/school-options/heading';
import type FetchService from '@linc-technologies/linced-common/services/fetch';
import type SessionService from '@linc-technologies/linced-common/services/session';
import type Store from '@linc-technologies/linced-common/services/store';
import config from '@linc-technologies/linced-common/utils/config';
import type { DynamicFieldMap } from '@linc-technologies/linced-common/utils/dynamic-fields';

// OptionsSchool is a school record, an async belongsTo of a school, or a
// stub with no fields.
export type OptionsSchool = object;

// Proto: schools.SchoolOptionsResponse. The API omits an empty list. The
// store serializers read the items.
interface SchoolOptionsResponse {
  options?: unknown[];
  optionHeadings?: unknown[];
  optionGroups?: unknown[];
}

// fieldOf gives the field of a school option, or undefined.
function fieldOf(
  school: OptionsSchool | null | undefined,
  option: string,
): Value | undefined {
  if (!option || !school) {
    return undefined;
  }
  // get() reads through the proxy of an async belongsTo.
  // eslint-disable-next-line ember/no-get
  const fields = get(school, 'fields') as DynamicFieldMap | undefined;
  return fields?.get(option) as Value | undefined;
}

/**
 * options service obtains school options in order to configure/restrict/show
 * certain UI elements based on specified school preferences.
 */
export default class OptionsService extends Service {
  // Services
  @service
  declare fetch: FetchService;

  @service
  declare session: SessionService;

  @service
  declare store: Store;

  // Properties

  // defaults gives the default value of each option. load replaces the map.
  @tracked defaults = new Map<string, unknown>();

  @tracked loaded = false;

  // Methods

  // value gives the value of a school option for the session school, or the
  // option default. It reads tracked state, so a getter or helper that calls
  // it updates when the school or the value changes.
  value(option: string): unknown {
    return this.schoolGet(this.session.school, option);
  }

  getValue(option: string): unknown {
    return this.value(option);
  }

  getDefault(option: string): unknown {
    return this.defaults.get(option);
  }

  async load(schoolId: string) {
    this.loaded = false;
    const response = (await this.fetch.request(
      `${config.APP.apiHost}/schools/${config.APP.apiPath}/schools/${schoolId}/options`,
    )) as SchoolOptionsResponse;

    const { store } = this;
    store.pushPayload('school-options/field', { options: response.options });
    store.pushPayload('school-options/heading', {
      optionHeadings: response.optionHeadings,
    });
    store.pushPayload('school-options/group', {
      optionGroups: response.optionGroups,
    });

    const options = store.peekAll<Field>('school-options/field');
    const defaults = new Map<string, unknown>();
    for (const option of options) {
      defaults.set(option.key, option.default?.value);
    }
    this.defaults = defaults;
    this.loaded = true;

    return {
      groups: store.peekAll<Group>('school-options/group'),
      headings: store.peekAll<Heading>('school-options/heading'),
      options,
    };
  }

  // schoolGet gives the value of a school option for school. When the school
  // has no value for the option, it gives the option default.
  schoolGet(school: OptionsSchool | null | undefined, option: string): unknown {
    const field = fieldOf(school, option);
    return field ? field.value : this.getDefault(option);
  }

  // waitUntilLoaded resolves when the options are loaded. After 3 seconds,
  // it loads them for the session school.
  waitUntilLoaded(): Promise<unknown> {
    if (this.loaded) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (this.loaded) {
          clearTimeout(timeout);
          clearInterval(interval);
          resolve(undefined);
        }
      }, 100);
      const timeout = setTimeout(() => {
        clearInterval(interval);
        resolve(this.load(this.session.schoolId));
      }, 3000);
    });
  }
}
